import { makeStyles } from '@mui/styles';
import Banner from 'apollo-react/components/Banner';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import qs from 'query-string';
import React, { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import Header from './Header';
import {
  clearDocumentViewer,
  closeActiveDeviationMessage,
  loadContent,
} from '../../actions/DocumentViewerActions';
import { setEnhancedModalOpen } from '../../actions/EnhancedModalActions';
import { clearPanelLayout } from '../../actions/PanelLayoutActions';
import { clearWorkflow } from '../../actions/WorkflowActions';
import FileViewer from '../../components/FileViewer';
import PanelLayout from '../../components/general/PanelLayout';
import Layout from '../../components/Layout';
import withSubmitFormDialog from '../../components/withSubmitFormDialog';
import { clearFormDesigner } from '../../form-designer/actions/form-manager.actions';
import DetailForm from '../../form-designer/components/DetailForm';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import ModalManager from '../../form-designer/ModalManager';
import { isFormLoading } from '../../form-designer/selectors/common.selectors';
import { clearValidationStorage } from '../../form-designer/utils/validationStorage.utils';
import { getPreventFormMounting } from '../../selectors/AppConfigsSelectors';
import { selectDocumentData, selectDocumentDeviation } from '../../selectors/DocumentViewer';
import { getEnhancedModalOpen } from '../../selectors/EnhancedModalSelectors';
import { getIsWorkflowRequestPending } from '../../selectors/WorkflowSelectors';
import { isTrue } from '../../utils/commonUtils';

const useStyles = makeStyles({
  panelContainer: {
    height: 'calc(100vh - 178px)',
  },
  docViewer: {
    paddingLeft: 30,
    height: '100%',
  },
});

const DocumentViewer = ({ location }) => {
  const documentData = useSelector(selectDocumentData);
  const deviationData = useSelector(selectDocumentDeviation);
  const isWorkflowRequestPending = useSelector(getIsWorkflowRequestPending);
  const isDetailFormLoading = useSelector(isFormLoading(FORM_KEYS.DETAIL));
  const enhancedModalOpen = useSelector(getEnhancedModalOpen);
  const preventFormMounting = useSelector(getPreventFormMounting);

  const classes = useStyles();
  const dispatch = useDispatch();

  const qsParams = qs.parse(location.search) || {};

  const {
    docId = documentData.DOC_ID,
    documentNumber = documentData.DOC_DOCUMENT_NO,
    documentRevision = documentData.DOC_DOCUMENT_REV,
    getLatestRevision,
    contentType = 0,
    isDocumentContent = true,
    certification = 0,
    entityType,
    entityViewName,
    formEntityName,
    formKey,
    itmId = documentData.DOC_ID,
    filename,
    isContentForHistory,
    buildNumber,
  } = qsParams;

  const isPortalContent = isTrue(qsParams.isPortalContent);
  const isBatchPrint = isTrue(qsParams.isBatchPrint);

  const params = {
    entityKey: entityType,
    parId: docId,
    itmId: isBatchPrint ? itmId : undefined,
    isDocumentContent: isPortalContent || isBatchPrint ? false : isDocumentContent,
    contentType,
    isContentForHistory,
    buildNumber,
    isPortalContent,
  };

  const documentTitle = documentData.DOC_TITLE;
  const titleText = `${documentNumber}${documentRevision && ` [${documentRevision}]`}`;

  const closeEnhancedModal = () => {
    dispatch(setEnhancedModalOpen(false));
  };

  useEffect(() => {
    if (documentNumber) {
      document.title = titleText;
    }
  }, [documentNumber, titleText]);

  useEffect(() => {
    dispatch(
      loadContent({
        documentData,
        entityType: isBatchPrint ? entityViewName : entityType,
        docId,
        documentNumber,
        documentRevision,
        getLatestRevision,
        contentType,
        certification,
        formKey,
        formEntityName,
        itmId,
        isPortalContent,
        isBatchPrint,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itmId]);

  useEffect(() => {
    return () => {
      batch(() => {
        dispatch(clearFormDesigner(FORM_KEYS.DETAIL));
        dispatch(clearFormDesigner(FORM_KEYS.MODAL));
        dispatch(clearDocumentViewer());
        dispatch(clearPanelLayout());
        dispatch(clearWorkflow());
        clearValidationStorage();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const panelsProps = {
    middlePanel: {
      isHideScroll: true,
    },
  };

  const headerProps = {
    docId,
    entityType,
    documentNumber,
    documentRevision,
    documentTitle,
    isPortalContent,
  };

  return (
    <Layout>
      {isWorkflowRequestPending && <Loader isInner />}
      <Grid container>
        <Grid item xs={12}>
          <Banner
            variant="warning"
            open={deviationData.open}
            onClose={() => dispatch(closeActiveDeviationMessage())}
            message={deviationData.ActiveDeviationMessage}
          />
          {!preventFormMounting && <Header {...headerProps} />}
          <div className={classes.panelContainer}>
            <PanelLayout
              leftPanelDisabled
              middlePanelChildren={
                <div className={classes.docViewer}>
                  {docId && <FileViewer filename={filename} params={params} />}
                </div>
              }
              rightPanelChildren={<DetailForm isLoading={isDetailFormLoading} />}
              panelsProps={panelsProps}
            />
          </div>
        </Grid>
      </Grid>
      <FormContext.Provider value={FORM_KEYS.MODAL}>
        <ModalManager open={enhancedModalOpen} onClose={closeEnhancedModal} />
      </FormContext.Provider>
    </Layout>
  );
};

export default withSubmitFormDialog(DocumentViewer);
